import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { RefObject, useCallback, useEffect } from 'react';
import { placeHolderData } from '../../config';

interface UseMediaSessionProps {
  audioRef: RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  handlePlayPause: () => void;
  handleNext: () => void;
  handlePrev: () => void;
  handleSeek?: (time: number) => void;
}

/**
 * Hook to integrate with the Media Session API for lock screen controls,
 * notification controls, and hardware media keys on mobile and desktop.
 */
export const useMediaSession = ({
  audioRef,
  isPlaying,
  handlePlayPause,
  handleNext,
  handlePrev,
  handleSeek,
}: UseMediaSessionProps) => {
  const { activePlaylist } = useMusicState();

  // Get current track info
  const getCurrentTrackInfo = useCallback(() => {
    if (!activePlaylist) return null;

    const currentTrack =
      activePlaylist.album.tracks[activePlaylist.currentIndex];
    if (!currentTrack) return null;

    const contributor =
      activePlaylist.album.type === 'playlist'
        ? currentTrack.contributors?.[0]
        : activePlaylist.album.contributors?.[0];



    console.log({
      title: currentTrack.name || 'Unknown Track',
      artist: contributor?.givenName || 'Unknown Artist',
      album: activePlaylist.album.albumName || 'Unknown Album',
      artwork:
        currentTrack?.album?.thumbnailUrl ||
        activePlaylist.album.albumArtUrl ||
        undefined,
    })

    return {
      title: currentTrack.name || 'Unknown Track',
      artist: contributor?.givenName || 'Unknown Artist',
      album: activePlaylist.album.albumName || 'Unknown Album',
      artwork:
        currentTrack?.album?.thumbnailUrl ||
        activePlaylist.album.albumArtUrl ||
        undefined,
    };
  }, [activePlaylist]);

  // Update media session metadata when track changes
  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      console.log('Media Session API not supported');
      return;
    }

    const trackInfo = getCurrentTrackInfo();
    if (!trackInfo) {
      navigator.mediaSession.metadata = null;
      return;
    }

    // Create artwork array with multiple sizes for better compatibility
    // Only include artwork if a valid URL exists
    const artworkSizes = [
      '96x96',
      '128x128',
      '192x192',
      '256x256',
      '384x384',
      '512x512',
    ];
    const artwork = trackInfo.artwork
      ? artworkSizes.map((size) => ({
          src: trackInfo.artwork!,
          sizes: size,
          type: 'image/jpeg',
        }))
      : undefined;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: trackInfo.title,
      artist: trackInfo.artist,
      album: trackInfo.album,
      ...(artwork && { artwork }),
    });

    console.log('📱 Media Session metadata updated:', trackInfo.title);
  }, [activePlaylist, activePlaylist?.currentIndex, getCurrentTrackInfo]);

  // Update playback state
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  // Set up action handlers
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    const audio = audioRef.current;

    // Play handler
    const handlePlay = () => {
      console.log('📱 Media Session: play action triggered');
      if (!isPlaying) {
        handlePlayPause();
      }
    };

    // Pause handler
    const handlePause = () => {
      console.log('📱 Media Session: pause action triggered');
      if (isPlaying) {
        handlePlayPause();
      }
    };

    // Previous track handler
    const handlePrevTrack = () => {
      console.log('📱 Media Session: previoustrack action triggered');
      handlePrev();
    };

    // Next track handler
    const handleNextTrack = () => {
      console.log('📱 Media Session: nexttrack action triggered');
      handleNext();
    };

    // Seek handler (for scrubbing on lock screen)
    const handleSeekTo = (details: MediaSessionActionDetails) => {
      if (details.seekTime !== undefined && audio) {
        console.log(
          '📱 Media Session: seekto action triggered',
          details.seekTime,
        );
        audio.currentTime = details.seekTime;
        if (handleSeek) {
          handleSeek(details.seekTime);
        }
      }
    };

    // Register action handlers
    try {
      navigator.mediaSession.setActionHandler('play', handlePlay);
      navigator.mediaSession.setActionHandler('pause', handlePause);
      navigator.mediaSession.setActionHandler('previoustrack', handlePrevTrack);
      navigator.mediaSession.setActionHandler('nexttrack', handleNextTrack);
      navigator.mediaSession.setActionHandler('seekto', handleSeekTo);
    } catch (error) {
      console.log('Media Session action handler error:', error);
    }

    // Cleanup
    return () => {
      try {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.setActionHandler('seekto', null);
      } catch (error) {
        console.log('Media Session cleanup error:', error);
      }
    };
  }, [
    audioRef,
    isPlaying,
    handlePlayPause,
    handleNext,
    handlePrev,
    handleSeek,
  ]);

  // Update position state for scrubbing UI on lock screen
  useEffect(() => {
    if (
      !('mediaSession' in navigator) ||
      !('setPositionState' in navigator.mediaSession)
    ) {
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    const updatePositionState = () => {
      try {
        if (
          audio.duration &&
          !isNaN(audio.duration) &&
          isFinite(audio.duration)
        ) {
          navigator.mediaSession.setPositionState({
            duration: audio.duration,
            playbackRate: audio.playbackRate || 1,
            position: audio.currentTime,
          });
        }
      } catch {
        // Position state update failed, ignore
      }
    };

    // Update position periodically while playing
    const interval = isPlaying ? setInterval(updatePositionState, 1000) : null;

    // Also update on time update events
    audio.addEventListener('timeupdate', updatePositionState);
    audio.addEventListener('loadedmetadata', updatePositionState);

    return () => {
      if (interval) clearInterval(interval);
      audio.removeEventListener('timeupdate', updatePositionState);
      audio.removeEventListener('loadedmetadata', updatePositionState);
    };
  }, [audioRef, isPlaying]);
};
